import type {
  ImaiFrame,
  ImaiMood,
  ImaiPoint,
  ImaiPose,
  ImaiStroke,
} from './types';

/** Shape helpers the bodies draw with. Angles are in degrees. */
export const S = Math.sin;
export const C = Math.cos;
export const abs = Math.abs;
export const max = Math.max;
export const min = Math.min;
export const R = Math.PI / 180;
export const lerp = (a: number, b: number, k: number) => a + (b - a) * k;
export const frac = (x: number) => x - Math.floor(x);
export const P0 = (): ImaiPose => ({ rot: 0, tx: 0, ty: 0, sy: 1 });

export function quad(
  p0: ImaiPoint,
  c: ImaiPoint,
  p1: ImaiPoint,
  n: number
): ImaiPoint[] {
  return Array.from({ length: n }, (_, i) => {
    const t = i / (n - 1);
    const a = (1 - t) ** 2;
    const b = 2 * (1 - t) * t;
    const d = t * t;
    return [a * p0[0] + b * c[0] + d * p1[0], a * p0[1] + b * c[1] + d * p1[1]];
  });
}

export function limb(
  x: number,
  y: number,
  ang: number,
  len: number,
  bend: number,
  n = 4
): ImaiPoint[] {
  const a = ang * R;
  const ex = x + len * C(a);
  const ey = y + len * S(a);
  return quad(
    [x, y],
    [(x + ex) / 2 - S(a) * bend, (y + ey) / 2 + C(a) * bend],
    [ex, ey],
    n
  );
}

export function arc(
  cx: number,
  cy: number,
  r: number,
  a0: number,
  a1: number,
  n: number
): ImaiPoint[] {
  return Array.from({ length: n }, (_, i) => {
    const a = (a0 + ((a1 - a0) * i) / (n - 1)) * R;
    return [cx + r * C(a), cy + r * S(a)];
  });
}

export function rotPts(
  pts: readonly ImaiPoint[],
  cx: number,
  cy: number,
  deg: number
): ImaiPoint[] {
  const a = deg * R;
  return pts.map(([x, y]) => [
    cx + (x - cx) * C(a) - (y - cy) * S(a),
    cy + (x - cx) * S(a) + (y - cy) * C(a),
  ]);
}

/** A closed superellipse: `p` 2 is an ellipse, higher is squarer. */
export function sq(
  cx: number,
  cy: number,
  hw: number,
  hh: number,
  p = 4,
  n = 24
): ImaiPoint[] {
  return Array.from({ length: n + 1 }, (_, k) => {
    const u = (k / n) * Math.PI * 2 - Math.PI / 2;
    const cu = C(u);
    const su = S(u);
    return [
      cx + hw * Math.sign(cu) * abs(cu) ** (2 / p),
      cy + hh * Math.sign(su) * abs(su) ** (2 / p),
    ];
  });
}

/**
 * A tapering ear or leaf: a centreline from the base that curls by `bend`
 * degrees towards the tip, outlined `w0` wide at the base.
 */
export function earShape(
  bx: number,
  by: number,
  ang: number,
  bend: number,
  len: number,
  w0: number
) {
  const line: ImaiPoint[] = [[bx, by]];
  for (let k = 1; k <= 7; k++) {
    const a = (ang + (bend * k * k) / 49) * R;
    const [px, py] = line[k - 1] ?? [bx, by];
    line.push([px + (S(a) * len) / 7, py - (C(a) * len) / 7]);
  }
  const side = (sign: number) =>
    line.map(([px, py], k): ImaiPoint => {
      const [qx, qy] = line[Math.min(7, k + 1)] ?? [px, py];
      const [ox, oy] = line[Math.max(0, k - 1)] ?? [px, py];
      const dx = qx - ox;
      const dy = qy - oy;
      const d = Math.hypot(dx, dy) || 1;
      const w = w0 * Math.sqrt(max(0, 1 - (k / 7) ** 3));
      return [px - (dy / d) * w * sign, py + (dx / d) * w * sign];
    });
  const left = side(1);
  return {
    outline: [...left, ...side(-1).reverse(), ...left.slice(0, 1)],
    inner: line.slice(1, 6),
  };
}

export const disc = (cx: number, cy: number, r: number) =>
  arc(cx, cy, r, -90, 270, 17);

export const hexCore = (r: number, rot: number, n = 6): ImaiPoint[] =>
  Array.from({ length: n + 1 }, (_, k) => {
    const a = ((k * 360) / n - 90 + rot) * R;
    return [50 + r * C(a), 52 + r * S(a)];
  });

export const scaleAt = (
  pts: readonly ImaiPoint[],
  cx: number,
  cy: number,
  k: number
): ImaiPoint[] => pts.map(([x, y]) => [cx + (x - cx) * k, cy + (y - cy) * k]);

/** A solid body-colour core to put the eyes on. */
export const core = (pts: ImaiPoint[], n = 28): ImaiStroke => ({
  c: 'a',
  fill: true,
  pts,
  n,
});

/** Fills the strokes at these indexes solid. */
export function fillAt(frame: ImaiFrame, ...indexes: number[]): ImaiFrame {
  for (const index of indexes) {
    const stroke = frame.strokes[index];
    if (stroke) frame.strokes[index] = { ...stroke, fill: true };
  }
  return frame;
}

/** How much a solid core swells with the mood. */
export function moodK(mood: ImaiMood, t: number): number {
  if (mood === 'happy') return 1.06 + 0.03 * abs(S(t * 6));
  if (mood === 'surprised') return 1.12;
  if (mood === 'paused') return 0.9;
  if (mood === 'talking') return 1 + 0.05 * max(0, S(t * 10));
  return 1 + 0.02 * S(t * 1.6);
}

/** A round dot: a stroke with two almost equal points. */
export const dot = (x: number, y: number): ImaiPoint[] => [
  [x, y],
  [x + 0.01, y],
];
